import {FontAwesomeIcon} from '@fortawesome/react-fontawesome';
import LetrusLoader from 'components/LetrusLoader';
import SidebarButton from 'components/SidebarButton';
import Uploader from 'components/Uploader';
// eslint-disable-next-line
import 'cropperjs/dist/cropper.css';
import {useEffect, useRef, useState} from 'react';
import Cropper, {ReactCropperElement} from 'react-cropper';
import {useParams} from 'react-router-dom';
import {OCRCompositionService} from 'store/services/compositionServices';
import {convertFilesToBase64String} from 'utils/functions/convertFilesToBase64String';
import useTrackEvent from 'utils/hooks/useTrackEvent';
import {useUpload} from 'utils/hooks/useUpload';
import {CompositionDetailExtended} from 'utils/types/compositions';
import {Button, InlineAlert, Modal} from 'letrus-ui';
import styles from './SubmitOCRModal.module.scss';

export interface SubmitOCRModalProps {
  id: string;
  onClose(): void;
  composition?: ImmutableMap<CompositionDetailExtended>;
}

interface PreviewImage {
  image: string;
  active: boolean;
}

function SubmitOCRModal({
  id,
  onClose,
  composition,
}: SubmitOCRModalProps): JSX.Element {
  const {composition_id} = useParams();
  const {trackEvent} = useTrackEvent();

  const [localSelectedFiles, setLocalSelectedFiles] = useState<File[]>([]);
  const [previewImages, setPreviewImages] = useState<PreviewImage[]>([]);
  const [isCropping, setIsCropping] = useState(false);
  const cropperRef = useRef<ReactCropperElement>(null);

  const [loadingOCRComposition, setloadingOCRComposition] =
    useState<boolean>(false);
  const [OCRCompositionsStatusCode, setOCRCompositionsStatusCode] =
    useState<string>('');
  const {UploadInput, handleUploadButtonClick} = useUpload({
    localSelectedFiles,
    setLocalSelectedFiles,
    maxNumberOfFiles: 3,
    maxFileSize: 4000,
  });

  const isSuccessOCRComposition = OCRCompositionsStatusCode === 'sucess';
  const isErrorOCRComposition = OCRCompositionsStatusCode === 'error';

  enum ButtonText {
    'sucess' = 'Concluir',
    'error' = 'Tentar novamente',
  }

  enum ModalHeaderText {
    'sucess' = 'Sucesso!',
    'error' = 'Erro!',
  }

  useEffect(() => {
    if (localSelectedFiles.length > 0) {
      const previewImagesUrl = localSelectedFiles.map((file, index) => {
        return {
          image: URL.createObjectURL(file),
          active: index === 0,
        };
      });

      setPreviewImages(previewImagesUrl);
    }
  }, [localSelectedFiles]);

  async function sendOCRImages() {
    setloadingOCRComposition(true);

    const base64Files = await convertFilesToBase64String(localSelectedFiles);

    /* sending only the first three files to the endpoint */
    const base64FilesToSend = base64Files.slice(0, 3);

    try {
      const OCRCompositionResponse = await OCRCompositionService(
        composition_id,
        base64FilesToSend,
      );

      const OCRCompositionStatusCode = OCRCompositionResponse?.status;

      setloadingOCRComposition(false);

      if (OCRCompositionStatusCode === 200) {
        setloadingOCRComposition(false);
        setOCRCompositionsStatusCode('sucess');

        trackEvent('used_ocr_feature', {
          test: {
            test_id: composition?.get('test_id'),
          },
          composition: {
            composition_id: composition?.get('id'),
          },
          count: {
            action_count: base64FilesToSend?.length,
          },
          supportingContent: {
            selected_text: OCRCompositionResponse?.data?.composition_raw ?? '',
          },
        });
      }
    } catch (error) {
      setloadingOCRComposition(false);
      setOCRCompositionsStatusCode('error');
    }

    setLocalSelectedFiles([]);
  }

  function onCropFinish() {
    if (!previewImages.find((image) => image.active) || !cropperRef.current) {
      setIsCropping(false);
      return;
    }

    const activeImageIndex = previewImages.findIndex((image) => image.active);

    cropperRef.current.cropper.getCroppedCanvas().toBlob((croppedImageBlob) => {
      if (!croppedImageBlob) {
        setIsCropping(false);
        return;
      }

      setLocalSelectedFiles(
        localSelectedFiles.map((current, index) => {
          if (index === activeImageIndex) {
            const croppedImage = new File([croppedImageBlob], current.name, {
              type: current.type,
            });
            setIsCropping(false);
            return croppedImage;
          }

          return current;
        }),
      );
      setIsCropping(false);
    });
  }

  return (
    <Modal id={id} childClassName={styles.container}>
      <h2 className={styles.title}>
        {ModalHeaderText[OCRCompositionsStatusCode] ?? 'Insira a foto'}
      </h2>

      {previewImages.length > 0 &&
        !OCRCompositionsStatusCode &&
        !loadingOCRComposition &&
        !isCropping && (
          <p className={styles.actionsCropSubtitle} data-testid="subtitle">
            Recorte apenas a área com o texto da redação. Evite bordas,
            desenhos, ou partes extras da folha. Clique no botão da direita para
            recortar agora!
          </p>
        )}

      <hr />

      <div className={styles.uploadContent}>
        {!loadingOCRComposition &&
          !OCRCompositionsStatusCode &&
          !previewImages.length && (
            <>
              <Uploader
                name="files"
                accept=".jpg,.jpeg,.webp,.bpm"
                maxNumberOfFiles={3}
                maxFileSize="04MB"
                setLocalSelectedFiles={setLocalSelectedFiles}
                localSelectedFiles={localSelectedFiles}
              />

              <div className={styles.alert}>
                <FontAwesomeIcon icon="circle-exclamation" color="#C4444D" />
                <strong data-testid="alert">
                  O texto no editor será substituído pelo texto da(s)
                  imagem(ens)
                </strong>
              </div>
            </>
          )}

        {previewImages.length > 0 &&
          !OCRCompositionsStatusCode &&
          !loadingOCRComposition &&
          !isCropping && (
            <>
              <div className={styles.actionsWrapper}>
                {UploadInput}
                <SidebarButton
                  icon="trash"
                  data-testid="removeSelectedImageOCR"
                  htmlFor="removeSelectedImageOCR"
                  disabled={!previewImages.find((image) => image.active)}
                  onClick={() => {
                    if (previewImages.length <= 1) {
                      setLocalSelectedFiles([]);
                      setPreviewImages([]);
                      return;
                    }
                    const activeImageIndex = previewImages.findIndex(
                      (image) => image.active,
                    );
                    setLocalSelectedFiles(
                      localSelectedFiles.filter(
                        (_, index) => index !== activeImageIndex,
                      ),
                    );
                  }}
                />
                <SidebarButton
                  data-testid="addImagesOCR"
                  icon={['fad', 'rectangle-history-circle-plus']}
                  htmlFor="addImagesOCR"
                  disabled={previewImages.length >= 3}
                  onClick={handleUploadButtonClick}
                />
                <SidebarButton
                  data-testid="cropSelectedImageOCR"
                  icon={['fad', 'crop']}
                  htmlFor="cropSelectedImageOCR"
                  disabled={!previewImages.find((image) => image.active)}
                  onClick={() => setIsCropping(true)}
                />
              </div>

              <div className={styles.previewContainer}>
                <img
                  className={styles.selectedPreviewImage}
                  src={
                    previewImages.find((image) => image.active)?.image ??
                    previewImages[0].image
                  }
                  alt="Prévia da imagem selecionada"
                />

                <div
                  className={styles.selectorPreviewImages}
                  data-testid="selector-preview"
                >
                  {previewImages.slice(0, 3).map((previewImage, index) => (
                    <button
                      className={styles.selectorPreviewImage}
                      type="button"
                      key={previewImage.image}
                      onClick={() => {
                        setPreviewImages((prev) => {
                          const newPreviewImage = [...prev];

                          newPreviewImage.forEach((image) => {
                            image.active = false;
                          });

                          newPreviewImage[index].active = true;

                          return newPreviewImage;
                        });
                      }}
                    >
                      <img
                        className={
                          previewImage.active
                            ? styles.previewImageActive
                            : styles.previewImage
                        }
                        src={previewImage.image}
                        alt="Prévia da imagem"
                      />
                    </button>
                  ))}
                </div>
              </div>
            </>
          )}

        {isCropping && (
          <>
            <div className={styles.previewContainer} data-testid="cropPreview">
              {!!previewImages.find((image) => image.active)?.image &&
                isCropping && (
                  <Cropper
                    src={previewImages.find((image) => image.active)?.image}
                    className={styles.selectedPreviewImage}
                    guides={false}
                    ref={cropperRef}
                  />
                )}
            </div>
          </>
        )}

        {loadingOCRComposition && (
          <div className={styles.loadingOCRComposition}>
            <LetrusLoader />

            <div className={styles.contentLoadingOCRComposition}>
              <FontAwesomeIcon icon="clock" color="#4574B9" />
              <strong>Carregando...</strong>
            </div>

            <span>Isso pode demorar alguns segundos!</span>
          </div>
        )}

        {isSuccessOCRComposition && (
          <div className={styles.contentStatusOCRComposition}>
            <FontAwesomeIcon icon="check-circle" color="#2fac66" size="3x" />

            <div className={styles.textStatusOCRComposition}>
              <span>O seu texto foi transcrito com sucesso,</span>
              <strong>lembre-se de revisar o texto</strong>
            </div>
          </div>
        )}

        {isErrorOCRComposition && (
          <div className={styles.contentStatusOCRComposition}>
            <FontAwesomeIcon
              icon="triangle-exclamation"
              color="#962f36"
              size="3x"
            />

            <div className={styles.textStatusOCRComposition}>
              <span>O seu texto não foi transcrito corretamente,</span>
              <strong>tente novamente</strong>
            </div>
          </div>
        )}
      </div>

      <hr />

      {!loadingOCRComposition && !isCropping && (
        <>
          <div className={styles.alertReview} data-testid="alertReview">
            <InlineAlert backgroundColor="#ffeac4" mainColor="#f3af31">
              <p>
                Esta ferramenta ajuda a transformar seu texto manuscrito em
                digital. Ela acerta quase tudo, mas pode errar algumas palavras
                ou letras. Por isso, revise sempre antes de enviar!
              </p>
            </InlineAlert>
          </div>

          <div className={styles.buttons}>
            <Button variant="secondary" onClick={() => onClose()}>
              Cancelar
            </Button>
            <Button
              variant="primary"
              onClick={
                isSuccessOCRComposition || isErrorOCRComposition
                  ? onClose
                  : sendOCRImages
              }
            >
              {ButtonText[OCRCompositionsStatusCode] ?? 'Inserir'}
            </Button>
          </div>
        </>
      )}

      {isCropping && (
        <div className={styles.cropButtons}>
          <Button variant="secondary" onClick={() => setIsCropping(false)}>
            Cancelar
          </Button>

          <Button variant="primary" onClick={() => onCropFinish()}>
            OK
          </Button>
        </div>
      )}
    </Modal>
  );
}

export default SubmitOCRModal;
